import { orgAccountApiReferrer } from '#client/org-account-api.ts'
import { formatNullableTimestamp } from '#client/format-timestamp.ts'
import { type Handle, css } from 'remix/component'
import { readAppSession } from '#client/app-session-context.tsx'
import { on } from '#client/event-mixin.ts'
import { navigate, readCurrentRouterHref } from '#client/client-router.tsx'
import { replaceLocation } from '#client/replace-location.ts'
import { createDoubleCheck } from '#client/double-check.ts'
import { createRouteData, routeDataRedirect } from '#client/route-data.tsx'
import { acceptedEmailVerificationDelivery } from '#universal/email-verification-delivery.ts'
import { readJson } from '#client/routes/account-approval-shared.ts'
import { createAccountEmailDestinations } from '#client/routes/account-email-destinations-client.ts'
import { renderAccountEmailDestinationsPanel } from '#client/routes/account-email-destinations-panel.tsx'
import {
	AccountManagementMessage,
	AccountManagementShell,
	AccountPageHeader,
} from '#client/routes/account-management-components.tsx'
import {
	renderEmailVerificationPrompt,
	requestResendVerification,
} from '#client/routes/email-verification-prompt.tsx'
import {
	RecordTable,
	RecordTableSearch,
	RecordTableSelect,
	recordStampCss,
} from '#client/routes/record-table.tsx'
import { colors, spacing, typography } from '#universal/styles/tokens.ts'
import { isUnlimitedLimit } from '#universal/plans.ts'
import { getGhostButtonCss } from '#universal/styles/style-primitives.ts'
import {
	type AccountEmailDestinationsLoaderData,
	type AccountEmailLoaderData,
	type AccountEmailMessageDetail,
} from '#universal/loader-data.ts'
import { renderAccountEmailDetail } from './account-email-detail.tsx'
import {
	type ClassificationFilter,
	type ClassifyState,
	type DeleteState,
	type PageStatus,
	accountEmailDestinationsLocationKey,
	accountEmailRouteLoader,
	buildEmailApiRequestUrl,
	fetchAccountEmailDestinations,
	clampedCellCss,
	directionLabel,
	emailRoute,
	getDataKey,
	messageDate,
	quarantinedBadgeCss,
	readClassificationFilter,
	readPage,
	readSearchQuery,
	statusLabel,
	subjectCellCss,
	truncatedTextCss,
} from './account-email-shared.ts'

export { accountEmailRouteLoader }

function formatEmailLimit(limit: number) {
	return isUnlimitedLimit(limit) ? 'Unlimited' : String(limit)
}

export function AccountEmailRoute(handle: Handle) {
	let data: AccountEmailLoaderData | null = null
	let message: string | null = null
	let messageTone: 'error' | 'info' = 'info'
	let classifyState: ClassifyState = 'idle'
	let deleteState: DeleteState = 'idle'
	const deleteMessageCheck = createDoubleCheck(handle)
	let resendStatus: 'idle' | 'sending' = 'idle'
	let resendMessage: string | null = null
	let resendTone: 'error' | 'info' = 'info'
	let resendAccepted = false
	/** Payload last applied from the route data snapshot (mutations update `data` directly). */
	let appliedPayload: AccountEmailLoaderData | null = null
	let appliedDestinations: AccountEmailDestinationsLoaderData | null = null
	let appliedError: Error | null = null
	const accountEmailDestinations = createAccountEmailDestinations(handle)
	const emailData = createRouteData({
		key: 'accountEmail',
		locationKey: getDataKey,
		async load(href, signal) {
			const response = await fetch(buildEmailApiRequestUrl(href), {
				headers: { Accept: 'application/json' },
				credentials: 'include',
				referrer: orgAccountApiReferrer(href),
				signal,
			})
			if (response.status === 401) return routeDataRedirect('/login')
			const payload = await readJson<AccountEmailLoaderData>(response)
			if (!response.ok || !payload?.ok) {
				throw new Error('Unable to load your email inbox.')
			}
			return payload
		},
	})
	const destinationsData = createRouteData({
		key: 'accountEmailDestinations',
		locationKey: () => accountEmailDestinationsLocationKey,
		async load(_href, signal) {
			const result = await fetchAccountEmailDestinations(signal)
			if (result.kind === 'unauthorized') return routeDataRedirect('/login')
			return result.payload
		},
	})

	const secondaryButtonCss = getGhostButtonCss({ size: 'sm' })

	function getCurrentHref() {
		return readCurrentRouterHref(handle)
	}

	function getCurrentSearch() {
		return new URL(getCurrentHref(), 'http://localhost').search
	}

	function buildHrefWithUpdatedSearch(search: string) {
		const nextUrl = new URL(getCurrentHref(), 'http://localhost')
		if (search) nextUrl.searchParams.set('q', search)
		else nextUrl.searchParams.delete('q')
		nextUrl.searchParams.delete('page')
		return `${nextUrl.pathname}${nextUrl.search}`
	}

	function buildHrefWithPage(page: number) {
		const nextUrl = new URL(getCurrentHref(), 'http://localhost')
		if (page > 1) nextUrl.searchParams.set('page', String(page))
		else nextUrl.searchParams.delete('page')
		return `${nextUrl.pathname}${nextUrl.search}`
	}

	function buildHrefWithClassification(filter: ClassificationFilter) {
		const nextUrl = new URL(getCurrentHref(), 'http://localhost')
		if (filter === 'all') nextUrl.searchParams.delete('classification')
		else nextUrl.searchParams.set('classification', filter)
		nextUrl.searchParams.delete('page')
		return `${nextUrl.pathname}${nextUrl.search}`
	}

	function applyPayload(payload: AccountEmailLoaderData, href: string) {
		data = payload
		deleteMessageCheck.reset()
		const selectedId = emailRoute.getSelection(href).selectedId
		message =
			payload.emailVerified && selectedId && !payload.selectedMessage
				? 'Message not found.'
				: null
		messageTone = selectedId && !payload.selectedMessage ? 'error' : 'info'
	}

	async function handleResendVerification() {
		resendStatus = 'sending'
		resendMessage = null
		resendTone = 'info'
		handle.update()

		try {
			const result = await requestResendVerification()
			if (!result.ok && result.unauthorized) {
				window.location.assign('/login')
				return
			}
			resendTone = result.ok ? 'info' : 'error'
			resendMessage = result.message
			if (result.ok) {
				resendAccepted = true
			}
		} catch {
			resendTone = 'error'
			resendMessage = 'Unable to send the verification email.'
		} finally {
			resendStatus = 'idle'
			handle.update()
		}
	}

	async function deleteSelectedMessage() {
		const selected = data?.selectedMessage
		if (!selected || classifyState !== 'idle' || deleteState !== 'idle') return
		deleteState = 'deleting'
		message = null
		handle.update()
		try {
			const response = await fetch(buildEmailApiRequestUrl(getCurrentHref()), {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				credentials: 'include',
				body: JSON.stringify({
					action: 'delete',
					message_id: selected.id,
				}),
			})
			if (response.status === 401) {
				window.location.assign('/login')
				return
			}
			const payload = await readJson<
				AccountEmailLoaderData & { error?: string; ok?: boolean }
			>(response)
			if (!response.ok || !payload?.ok) {
				throw new Error(payload?.error || 'Unable to delete message.')
			}
			const listHref = emailRoute.buildListHref(
				getCurrentSearch(),
				getCurrentHref(),
			)
			applyPayload(payload, listHref)
			deleteState = 'idle'
			message = 'Message deleted.'
			messageTone = 'info'
			handle.update()
			navigate(listHref)
		} catch (error) {
			deleteState = 'idle'
			deleteMessageCheck.reset()
			message =
				error instanceof Error ? error.message : 'Unable to delete message.'
			messageTone = 'error'
			handle.update()
		}
	}

	async function classifySelectedMessage(
		classification: 'accepted' | 'quarantined',
	) {
		const selected = data?.selectedMessage
		if (
			!selected ||
			selected.direction !== 'inbound' ||
			classifyState !== 'idle' ||
			deleteState !== 'idle'
		)
			return
		classifyState = 'saving'
		message = null
		handle.update()
		try {
			const response = await fetch(buildEmailApiRequestUrl(getCurrentHref()), {
				method: 'POST',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				credentials: 'include',
				body: JSON.stringify({
					action: 'classify',
					message_id: selected.id,
					classification,
				}),
			})
			if (response.status === 401) {
				window.location.assign('/login')
				return
			}
			const payload = await readJson<
				AccountEmailLoaderData & { error?: string; ok?: boolean }
			>(response)
			if (!response.ok || !payload?.ok) {
				throw new Error(
					payload?.error || 'Unable to update message classification.',
				)
			}
			applyPayload(payload, getCurrentHref())
			classifyState = 'idle'
			message =
				classification === 'quarantined'
					? 'Marked as spam.'
					: 'Marked as not spam.'
			messageTone = 'info'
			handle.update()
		} catch (error) {
			classifyState = 'idle'
			message =
				error instanceof Error
					? error.message
					: 'Unable to update message classification.'
			messageTone = 'error'
			handle.update()
		}
	}

	return () => {
		const currentHref = getCurrentHref()
		const snapshot = emailData.read(handle, currentHref)
		const destinationsSnapshot = destinationsData.read(handle, currentHref)
		if (snapshot.data && snapshot.data !== appliedPayload) {
			appliedPayload = snapshot.data
			applyPayload(snapshot.data, currentHref)
		}
		if (
			destinationsSnapshot.data &&
			destinationsSnapshot.data !== appliedDestinations
		) {
			appliedDestinations = destinationsSnapshot.data
			accountEmailDestinations.applyPayload(destinationsSnapshot.data)
		}
		if (snapshot.error && snapshot.error !== appliedError) {
			appliedError = snapshot.error
			message = snapshot.error.message
			messageTone = 'error'
		} else if (
			destinationsSnapshot.error &&
			destinationsSnapshot.error !== appliedError
		) {
			appliedError = destinationsSnapshot.error
			message = destinationsSnapshot.error.message
			messageTone = 'error'
		}
		const pending = snapshot.kind === 'pending'
		const status: PageStatus =
			snapshot.kind === 'error'
				? 'error'
				: pending && data === null
					? 'loading'
					: 'ready'

		const selectedMessageId = emailRoute.getSelection(currentHref).selectedId
		const selectedMessage: AccountEmailMessageDetail | null =
			data?.selectedMessage ?? null
		const totalPages = data
			? Math.max(1, Math.ceil(data.total / data.pageSize))
			: 1
		const currentPage = data?.page ?? readPage(currentHref)
		const searchQuery = data?.query ?? readSearchQuery(currentHref)
		const classificationFilter =
			data?.classification === 'quarantined'
				? 'quarantined'
				: readClassificationFilter(currentHref)
		const showUnverified = data != null && !data.emailVerified

		return (
			<AccountManagementShell
				maxWidth="min(100%, 92rem)"
				busy={pending && data !== null}
			>
				<AccountPageHeader
					title="Email inbox"
					description="Browse inbound and outbound messages for your platform email address. Compose and reply through Kody agents. Manage the addresses emailSend may use here."
					currentHref={currentHref}
				/>
				{status === 'loading' ? (
					<p mix={css({ color: colors.textMuted, margin: 0 })}>
						Loading email inbox…
					</p>
				) : null}
				{message ? (
					<AccountManagementMessage
						tone={
							status === 'error' || messageTone === 'error' ? 'error' : 'info'
						}
					>
						{message}
					</AccountManagementMessage>
				) : null}
				{data && !showUnverified ? (
					<>
						{data.usage || data.inboxAddress ? (
							<div
								mix={css({
									display: 'grid',
									gap: spacing.sm,
									marginBottom: spacing.md,
								})}
							>
								{data.inboxAddress ? (
									<p mix={css({ margin: 0, color: colors.text })}>
										Inbox address:{' '}
										<code mix={css({ overflowWrap: 'anywhere' })}>
											{data.inboxAddress}
										</code>
									</p>
								) : null}
								{data.usage ? (
									<p
										mix={css({
											margin: 0,
											color: colors.textMuted,
											fontSize: typography.fontSize.sm,
										})}
									>
										Plan {data.usage.plan}: {data.usage.stored_messages.count}/
										{data.usage.stored_messages.limit} stored ·{' '}
										{data.usage.receives_today.count}/
										{formatEmailLimit(data.usage.receives_today.limit)} received
										today · {data.usage.sends_today.count}/
										{formatEmailLimit(data.usage.sends_today.limit)} sent today
									</p>
								) : null}
							</div>
						) : null}
						{appliedDestinations
							? renderAccountEmailDestinationsPanel({
									destinations: accountEmailDestinations.destinations,
									additionalRemaining:
										accountEmailDestinations.additionalRemaining,
									additionalLimit: accountEmailDestinations.additionalLimit,
									draftEmail: accountEmailDestinations.draftEmail,
									status: accountEmailDestinations.status,
									message: accountEmailDestinations.message,
									tone: accountEmailDestinations.tone,
									pendingId: accountEmailDestinations.pendingId,
									onDraftEmailInput: accountEmailDestinations.updateDraftEmail,
									onAddSubmit: (event) => {
										void accountEmailDestinations.handleAddSubmit(event)
									},
									onResend: (id) => {
										void accountEmailDestinations.resend(id)
									},
									onSetDefault: (id) => {
										void accountEmailDestinations.setDefault(id)
									},
									onRemove: (id) => {
										void accountEmailDestinations.remove(id)
									},
								})
							: null}
						<RecordTable
							mode="expand"
							busy={pending}
							ariaLabel="Inbox messages"
							selectedId={selectedMessageId}
							countLabel={
								status === 'ready'
									? `${data.total} ${data.total === 1 ? 'message' : 'messages'}`
									: undefined
							}
							emptyLabel={
								searchQuery || classificationFilter !== 'all'
									? 'No messages match the current filters.'
									: 'No messages in your inbox yet.'
							}
							toolbar={
								<>
									<RecordTableSearch
										label="Search messages"
										placeholder="Search subject or from"
										value={searchQuery}
										onInput={(value) => {
											replaceLocation(buildHrefWithUpdatedSearch(value))
										}}
									/>
									<RecordTableSelect
										label="Filter messages by classification"
										value={classificationFilter}
										onChange={(value) => {
											replaceLocation(
												buildHrefWithClassification(
													value === 'quarantined' ? 'quarantined' : 'all',
												),
											)
										}}
									>
										<option value="all">All messages</option>
										<option value="quarantined">Quarantined</option>
									</RecordTableSelect>
								</>
							}
							columns={[
								{ key: 'subject', label: 'Subject', primary: true },
								{ key: 'from', label: 'From', drop: 1 },
								{ key: 'direction', label: 'Direction', drop: 3 },
								{ key: 'status', label: 'Status', drop: 2 },
								{ key: 'date', label: 'Date' },
							]}
							rows={data.messages.map((emailMessage) => ({
								id: emailMessage.id,
								href: emailRoute.buildDetailHref(
									emailMessage.id,
									getCurrentSearch(),
									getCurrentHref(),
								),
								cells: {
									subject: (
										<span mix={css(subjectCellCss)}>
											<span mix={css(truncatedTextCss)}>
												{emailMessage.subject || '(no subject)'}
											</span>
											{emailMessage.classification === 'quarantined' ? (
												<span
													title={
														emailMessage.classification_reason ?? 'Quarantined'
													}
													mix={css(quarantinedBadgeCss)}
												>
													Quarantined
												</span>
											) : null}
										</span>
									),
									from: (
										<span mix={css(clampedCellCss)}>
											{emailMessage.from_address ??
												emailMessage.envelope_from ??
												'Unknown sender'}
										</span>
									),
									direction: directionLabel(emailMessage.direction),
									status: statusLabel(emailMessage),
									date: (
										<span mix={css(recordStampCss)}>
											{formatNullableTimestamp(
												messageDate(emailMessage),
												'Unknown',
											)}
										</span>
									),
								},
							}))}
							footer={
								totalPages > 1 ? (
									<div
										mix={css({
											display: 'flex',
											gap: spacing.sm,
											alignItems: 'center',
											justifyContent: 'center',
										})}
									>
										<button
											type="button"
											disabled={currentPage <= 1}
											mix={[
												on('click', () => {
													replaceLocation(buildHrefWithPage(currentPage - 1))
												}),
												css(secondaryButtonCss),
											]}
										>
											Previous
										</button>
										<p
											mix={css({
												margin: 0,
												color: colors.textMuted,
												fontSize: typography.fontSize.xs,
											})}
										>
											Page {currentPage} of {totalPages}
										</p>
										<button
											type="button"
											disabled={currentPage >= totalPages}
											mix={[
												on('click', () => {
													replaceLocation(buildHrefWithPage(currentPage + 1))
												}),
												css(secondaryButtonCss),
											]}
										>
											Next
										</button>
									</div>
								) : null
							}
							record={
								selectedMessage
									? renderAccountEmailDetail({
											selectedMessage,
											classifyState,
											deleteState,
											deleteCheck: deleteMessageCheck,
											onClassify: (classification) => {
												void classifySelectedMessage(classification)
											},
											onDelete: () => {
												void deleteSelectedMessage()
											},
										})
									: null
							}
						/>
					</>
				) : null}
				{data && showUnverified ? (
					<>
						{data.inboxAddress ? (
							<p mix={css({ margin: 0 })}>
								Your inbox address will be <code>{data.inboxAddress}</code>{' '}
								after verification.
							</p>
						) : null}
						{renderEmailVerificationPrompt({
							email: data.email,
							description:
								'Verify your account email to browse stored messages. MCP access and email features stay locked until this account email is verified.',
							delivery: resendAccepted
								? acceptedEmailVerificationDelivery()
								: (readAppSession(handle)?.session?.emailVerificationDelivery ??
									null),
							resendStatus,
							resendMessage,
							resendTone,
							onResend: () => {
								void handleResendVerification()
							},
							secondaryHref: '/pending-verification',
							secondaryLabel: 'Verification page',
						})}
					</>
				) : null}
			</AccountManagementShell>
		)
	}
}
